import { useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import useMediaQuery from '@mui/material/useMediaQuery'

import {
  HomeHeyNicChat,
  HomeHeyNicComment,
  homeChatComment,
  chatPromptKeys,
  homeChatPrompt,
  homeChatSuggestions,
  strandedHelpKeys,
  HOME_CHAT_VIEWPORT,
} from '../heyNic'
import useActionItems from './useActionItems'
import ActionItems from './ActionItems'
import { AlertsStack } from './styles'

/*
 * The Home screen's alerts slot, which is also where the AI chatbot lives.
 *
 * Three arrangements, all driven off the SAME active-alert keys:
 *
 *   AI on, wide      → the chatbot alone. Its opening comment is the alerts'
 *                      body copy, so the box would just repeat it. The one
 *                      exception is an alert carrying help the chatbot's pills
 *                      cannot offer (see strandedHelpKeys) — the box comes back
 *                      for that alert alone, since hiding it would leave its
 *                      help unreachable rather than merely repeated.
 *   AI on, narrow    → the comment card, with the alerts box below it. Keeping
 *                      both is deliberate (per the story) while we decide
 *                      whether the chat replaces them on mobile too.
 *   AI off           → the alerts box alone. This is the fallback design.
 *
 * `useActionItems` is called here, once, rather than inside ActionItems: the
 * narrow-with-AI case renders both surfaces, and the hook fetches pensions to
 * resolve its lump-sum key — two copies of it would mean two of that request and
 * two answers that could disagree.
 */
const HomeAlerts = ({ mode, store }) => {
  const wide = useMediaQuery(HOME_CHAT_VIEWPORT)

  const {
    person,
    spouse,
    userLogs,
    account,
    standardLimit,
    standardCatchup,
    additionalCatchup,
    getLimits,
    getAllPensions,
    employerAccounts,
    otherAccounts,
    institutionalAccount,
    isRequiredToReviewRetirementAccount,
    config: { isRetail, templateId, enableCatchupContributions, template },
  } = store

  /*
   * The contribution limits, fetched here rather than in ActionItems.
   *
   * They are interpolated into the catch-up copy, and that copy now has two
   * consumers: the alerts box AND the chatbot's opening comment. ActionItems
   * used to own this fetch, but on the wide AI path it never mounts — the
   * comment would have interpolated an unloaded `standardLimit` as $0, and
   * `standardLimit + maximumAmount` as $NaN, and stayed wrong unless some other
   * screen happened to have fetched limits already.
   */
  useEffect(() => {
    getLimits()
  }, [getLimits])

  const activeKeys = useActionItems({
    mode,
    person,
    account,
    isRetail,
    templateId,
    enableCatchupContributions,
    employerAccounts,
    otherAccounts,
    institutionalAccount,
    isRequiredToReviewRetirementAccount,
    getAllPensions,
    spouse,
    userLogs,
  })

  // Same gate every other Hey Nic entry point uses: the feature flag AND a
  // reachable AI service.
  const aiEnabled = !!(store.features?.enableHeyNic && store.aiStatus?.available)

  const commentCtx = {
    currentYear: dayjs().year(),
    standardLimit,
    standardCatchup,
    additionalCatchup,
    sponsorName: template?.sponsorName,
  }
  const comment = homeChatComment(activeKeys, commentCtx)

  // Resolved here alongside the comment, off the same keys, so the pills the
  // wide panel shows and the pills the handoff carries can't disagree.
  const suggestions = homeChatSuggestions(activeKeys)
  /*
   * Some of those pills are answered client-side rather than by the agent, and
   * the surface showing them needs to know which exchange owns them (see
   * hey-nic/salaryPrompt and hey-nic/spouseGoalPrompt). Derived from the same
   * single answer that chose the pills, because both exchanges label a pill
   * "Learn more" and only one may be listening for it.
   */
  const prompt = homeChatPrompt(activeKeys)
  const salaryPrompt = prompt === 'salary'
  const spouseGoalPrompt = prompt === 'spouseGoal'

  /*
   * Alerts whose help the pills cannot reach. On the wide layout they keep the
   * alerts box — rendered for them alone, and dropped from the chat's comment,
   * so each alert appears exactly once and every help icon still has a home.
   */
  const strandedKeys = strandedHelpKeys(activeKeys)
  // What the chat panel speaks for: everything the box is not carrying.
  const chatKeys = activeKeys.filter(key => !strandedKeys.includes(key))

  if (aiEnabled && wide) {
    /*
     * ONE shape for both cases, always the stack.
     *
     * Returning a bare panel when nothing is stranded would change the element
     * type at this position, and React would remount the panel — throwing away
     * the exchange the user is in the middle of, the pinned opener, and the
     * handover turn that exists precisely to carry them across this moment (see
     * HomeHeyNicChat). The box withdrawing must not cost the conversation, and
     * the box withdraws on exactly the transition the handover is for.
     *
     * AlertsStack is a bare flex column and HomeChatCard sizes itself, so the
     * wrapper costs nothing when it holds the panel alone.
     */
    return (
      <AlertsStack>
        <HomeHeyNicChat
          comment={homeChatComment(chatKeys, commentCtx)}
          suggestions={suggestions}
          salaryPrompt={salaryPrompt}
          spouseGoalPrompt={spouseGoalPrompt}
        />
        {strandedKeys.length > 0 && <ActionItems activeKeys={strandedKeys} />}
      </AlertsStack>
    )
  }

  return (
    <AlertsStack>
      {aiEnabled && (
        <HomeHeyNicComment
          comment={comment}
          suggestions={suggestions}
          promptKeys={chatPromptKeys(activeKeys)}
        />
      )}
      <ActionItems activeKeys={activeKeys} />
    </AlertsStack>
  )
}

HomeAlerts.propTypes = {
  /** Accumulation Home's retirement-horizon mode; gates one of the alerts. */
  mode: PropTypes.string,
}

export default inject('store')(observer(HomeAlerts))
